import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {Router} from '@angular/router';
import {PersonLogin, RoleName} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {PersonLoginService} from '../services/person-login-service';
import {PersonService} from '../services/person-service';
import {SelectionService} from '../services/selection-service';
import {StringUtils} from '../utils/string-utils';
import {Person} from './person';
import {PersonHeader} from './person-header';
import {PersonHeaderSearchCriteria} from './person-header-search-criteria';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

import { FormsModule } from '@angular/forms';


@Component({
    selector: 'person-header',
    templateUrl: './person-header.component.html',
    styleUrls: ['./person-header.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatProgressSpinner]
})
export class PersonHeaderComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly personService = inject(PersonService);
  private readonly selectionService = inject(SelectionService);
  private readonly router = inject(Router);


  public personLogin!: PersonLogin;
  readonly searchResults = signal<PersonHeader[]>(undefined!);
  public criteria!: PersonHeaderSearchCriteria;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly searchComplete = signal<boolean>(undefined!);
  readonly organizations = signal<SelectionItem[]>(undefined!);
  readonly maxPage = signal<number>(undefined!);

  ngOnInit(): void {
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.personLogin || this.personLoginService.hasRole(RoleName.LSBCS)) {
      this.router.navigate(['main']);
    } else if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.criteria = new PersonHeaderSearchCriteria();
      if (this.personLoginService.isInternalOrRsm()) {
        this.selectionService.getEmulatedOrganizations().subscribe({
          next: (results) => {
            this.selectionService.getManagedOutsideEngineeringOffices().subscribe({
              next: (outsideEngineerOffices) => {
                const allOrganizations = results;
                allOrganizations.push(...outsideEngineerOffices);
                this.organizations.set(allOrganizations.sort((first, second) => first.description.localeCompare(second.description)));
              }, error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error)
        });
      }
      this.criteria.orgUnitId = this.personLogin.person.primaryOrgUnitId;
      this.criteria.statusCode = 'ACTV';
      this.criteria.searchTerm = '';
      this.search();
    }
  }

  public searchPeople(): void {
    if (!this.personLogin || this.personLoginService.hasRole(RoleName.LSBCS)) {
      this.router.navigate(['main']);
    } else {
      if (!this.criteria.orgUnitId || this.criteria.orgUnitId.length == 0) {
        if (this.personLoginService.hasRole(RoleName.IECIT)) {
          this.search();
        }
      } else {
        this.search();
      }
    }
  }

  public previousPage(): void {
    this.criteria.pageNumber -= 1;
    this.getResultPage();
  }

  public nextPage(): void {
    this.criteria.pageNumber += 1;
    this.getResultPage();
  }

  public search(): void {
    this.criteria.pageNumber = 1;
    this.maxPage.set(1);
    this.getResultPage();
  }

  public getResultPage(): void {
    if (this.criteria.pageNumber <= this.maxPage()) {
      this.clearStatus();
      this.personService.search(this.criteria).subscribe(results => {
        this.searchResults.set(results);
        this.isBusy.set(false);
        this.searchComplete.set(true);
        if (results.length > 0) {
          this.maxPage.set(Math.ceil(results[0].recordCount / 20));
        }
      }, error => this.handleError(error));
    }
  }

  public handleKeyUp(keyEvent: any): void {
    if (keyEvent.keyCode === 13) {
      this.search();
    }
  }

  public selectPerson(person: PersonHeader) {
    this.router.navigate(['person/' + person.personId]);
  }

  public newPerson(): void {
    this.router.navigate(['person/new']);
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.searchComplete.set(false);
    this.searchResults.set(undefined!);
  }

  public hasRole(roleName: string) {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

}
